/**
 * Patients: the registry and the patient card.
 *
 * Composed into the public catalogue by `catalogue.en.ts`. Nothing imports
 * this module directly: consumers ask the catalogue, so how the messages are
 * stored stays this layer's business.
 */
export const patients = {
  "patient.documents.nationalType": "National document type",
  "patient.documents.nationalPolicyType": "National policy type",
  "patient.documents.insurerCoding": "Insurer directory code",
  "patient.documents.searchCoding": "Search the clinic profile dictionary",
  "patient.documents.clearCoding": "Clear dictionary selection",



  "patient.curator.edit": "Change curator",
  "patient.curator.title": "Curator",
  "patient.curator.none": "No curator assigned",
  "patient.curator.loading": "Loading curator",
  "patient.curator.notFound": "Patient was not found.",
  "patient.curator.unsaved": "Unsaved curator assignment",
  "patient.curator.cycle": "This assignment would create a cycle of curators.",
  "patient.curator.ineligible":
    "Choose a person with an active physician role in this clinic.",
  "patient.curator.stale":
    "The curator changed in another session. Reload the current assignment.",
  "patient.curator.failed": "Could not load or save the curator. Try again.",

  "patients.title": "Patients",
  "patients.loading": "Loading patients",
  "patients.tableLabel": "Patient registry",
  // The count is the SERVER's, over the whole filtered set, so it does not say
  // "shown" — a page carries at most `limit` of them.
  "patients.resultCountOne": "{count} patient",
  "patients.resultCountMany": "{count} patients",
  "patients.matchCountOne": "{count} matching patient",
  "patients.matchCountMany": "{count} matching patients",
  "patients.columnPatient": "Patient",
  "patients.columnCode": "Code",
  "patients.columnBirthDate": "Date of birth",
  "patients.columnSex": "Sex",
  "patients.columnStatus": "Status",
  "patients.columnUpdated": "Updated",
  "patients.columnExternalSystems": "External systems",
  "patients.columnAcquisition": "Acquisition",
  // The two kinds the Patient funnel can keep: a checklist over the
  // test-Patient marker, worded as what the rows ARE rather than as the flag.
  "patients.filterTestPatients": "Test patients",
  "patients.filterRealPatients": "Real patients",
  "patients.filterVipPatients": "VIP patients",
  "patients.filterRegularPatients": "Regular patients",
  // The identity funnel holds two checklists over two independent facts, so
  // each says what it is a choice of; a lone list needs no such line, because
  // the heading the panel drops from already says it.
  "patients.filterKindGroup": "Record kind",
  "patients.filterVipGroup": "VIP standing",
  // The two bounds of a column's civil-day range funnel. Each pair names its
  // column's fact, because four bare "From" fields in four popovers would all
  // read the same to a screen reader.
  "patients.birthFrom": "Born from",
  "patients.birthTo": "Born to",
  "patients.updatedFrom": "Updated from",
  "patients.updatedTo": "Updated to",
  // The column holds what a row can have DONE to it: kept in quick access,
  // and left by. Named for neither, because it now holds both.
  "patients.columnActions": "Actions",
  // The name and code are data: interpolated, never translated. Both names
  // carry the word the control shows, so the accessible name never drops the
  // label a sighted clinician would read aloud.
  "patients.cardNamed": "Open patient card for {name}, {code}",
  "patients.selectNamed": "Select {name}, {code}",
  // The server's disclosure under a row that answered a search through an
  // old, maiden or ended name; the name printed above stays the current one.
  "patients.matchedFormerName": "Matched a former name",
  "patients.selectedPatient": "Selected patient",
  "patients.openCard": "Open patient card",
  "patients.empty": "No patients yet",
  "patients.emptyDetail":
    "Patients registered in this clinic will appear here.",
  // The server refused the date itself, with its own code. Saying so beats a
  // generic failure for something it explained precisely.
  "patients.birthDateInvalid":
    "That date of birth is not a valid calendar date.",
  // The server refused a criterion the browser was willing to send — its
  // regular expressions are not the browser's. Retrying cannot help, so the
  // words point at the box that still holds the expression.
  "patients.criterionRefused":
    "The server refused one of these search expressions. Edit it to load the list again.",
  "patients.noMatches": "No patients match these filters",
  "patients.noMatchesDetail":
    "Adjust the column filters, or clear them to see everyone.",
  "patients.loadFailed": "Patients could not be loaded",

  "patient.code": "Patient code",
  "patient.birthDate": "Date of birth",
  "patient.acquisition": "Acquisition source",
  "patient.acquisition.kind.clinic": "Clinic",
  "patient.acquisition.kind.practitioner": "Practitioner",
  "patient.acquisition.kind.referralPartner": "Referral partner",
  "patient.acquisition.unresolved": "Legacy source (unresolved)",
  "patient.acquisition.capture": "Capture",
  "patient.acquisition.capture.manual": "Manual",
  "patient.acquisition.capture.onboardingLink": "Onboarding link",
  "patient.acquisition.capture.module": "External module",
  "patient.acquisition.capture.migration": "Legacy migration",
  "patient.acquisition.capture.correction": "Correction",
  "patient.acquisition.option": "{kind}: {name}",
  "patient.acquisition.select": "Select a source",
  "patient.acquisition.loading": "Loading sources…",
  "patient.acquisition.loadFailed": "Acquisition sources could not be loaded.",
  "patient.acquisition.correctionReason":
    "Reason for changing the acquisition source",
  "patient.acquisition.required": "Select an acquisition source.",
  "patient.rowDescription": "{code}, {birth}",
  "patient.administrativeSex": "Administrative sex",
  "patient.sex.female": "Female",
  "patient.sex.male": "Male",
  // The row's own lifecycle STATE. `inactive` on the wire reads as Archived
  // here because that is what a clinic browsing the registry calls it, and the
  // mapping from one to the other lives in `patients/lifecycleLabels.ts`.
  "patient.status.active": "Active",
  "patient.status.archived": "Archived",
  "patient.status.merged": "Merged",
  // The leaf saying how the chart came to be: whose Patient this is on
  // paper, the channel that captured it, and the charts it replaces or was
  // folded into.
  "patient.registration": "Registration",
  "patient.link.replaced-by": "Replaced by",
  "patient.link.replaces": "Replaces",
  "patient.preferredLanguage": "Preferred language",
  // What this document is, said once, in words the menu tile beside it does
  // NOT already carry: the tile names the destination, and a subtitle that
  // repeated it would be the same sentence twice on one screen.
  "patient.titleSheetSubtitle":
    "The civil and administrative record the clinic holds for this patient. Clinical findings live in the other sections of this chart.",
  // Reads as a sentence with the instant after it, so it carries no colon: a
  // separator inside the string would be read as a word by some screen readers
  // and would put the value out of reach of a test looking for it.
  // The navigation panel's shortcut tray. The namespace stays with Patients
  // because the dashboard's Patient ribbon below is its other consumer, but the
  // tray itself is generic: every kind it can show names itself here.
  "recent.title": "Recently opened",
  "recent.kindPatients": "Patients",
  // ENCOUNTER, the API's word, is the panel's word too. The product carries ONE
  // name for the thing a clinician conducts, in both locales; a second name for
  // it read as a second thing wherever the two surfaces met.
  "recent.kindEncounters": "Encounters",
  "recent.kindExternalEntries": "External entries",
  // Where a row LEADS, not just whose it is. A name alone would leave a screen
  // reader to discover the destination by pressing it, and would make a Patient
  // and an encounter with that Patient two links called the same thing. The
  // encounter's moment is appended by the component, because a kind whose
  // moment is missing must still read as a complete phrase.
  "recent.openPatient": "Open the chart for {name}",
  "recent.openEncounter": "Open the encounter with {name}",
  "recent.openExternalEntry": "Open the external entry of {name}",
  // The two things a clinician does to their OWN list. EACH NAMES THE ROW IT
  // ACTS ON, because the controls repeat once per row: a name that stopped at
  // "Add to favourites" would give a screen reader five identical buttons.
  //
  // The star is the glyph and FAVOURITES is the word, because the glyph alone
  // says nothing about what happens — a starred row leads the list and stays
  // there — and a word alone is not what a clinician's eye finds on a busy
  // morning.
  "recent.star": "Add {name} to favourites",
  "recent.unstar": "Remove {name} from favourites",
  // SAID IN FULL, and about the LIST. "Remove {name}", on a row that names a
  // person, is the one phrasing here that could be read as removing the
  // patient; nothing on this tray touches a record.
  "recent.forget": "Take {name} off the recently opened list",
  // The dashboard's ribbon of the same Patients names itself in full: it is
  // a region label nobody sees, so it cannot lean on a heading beside it.
  "recent.ribbonTitle": "Recently opened patients",
  // Where the block LEADS, not just whose it is. Every block opens the chart
  // on its records, and a name that stopped at the Patient would leave a
  // screen reader to discover the destination by pressing it.
  "recent.openCard": "Open EMR records for {name}, {code}",
  // The Patient rail on a clinical detail screen. Both states name themselves
  // rather than rendering an empty identity beside a real clinical document.
  "patient.contextLoading": "Loading patient…",
  "patient.contextUnavailable": "This patient’s details are unavailable.",
  "patient.notFound": "Patient not found",
  "patient.notFoundDetail": "This patient does not exist in your clinic.",
  // Two independent facts, so two labels. A single "Country" would invite one
  // control for both, which is how the two values start leaking into each other.
  "patient.citizenship": "Citizenship",
  "patient.countryOfResidence": "Country of residence",
  "patient.ageYears": "{years} years",
  // The identity card's compact spelling. Abbreviated because the card is a
  // narrow column; the registry keeps the spelled-out form above.
  "patient.ageYearsMonths": "{years} yr {months} mo",
  "patient.ageMonths": "{months} mo",
  "patient.birthDateInvalid":
    "That date of birth was refused. It cannot be in the future.",
  // Named in the leave prompt's list, so "you have unsaved changes" can be
  // answered by someone who knows which work it means.
  "patient.unsavedIntent": "Patient details",

  // The practitioner's own mark, as a surface REPORTS it rather than offers
  // it. Same word as the two acts above — favourites, not "starred", because
  // the star is the glyph and the word is what it means — but stated as a
  // fact: a registry row cannot be pressed, and a name that read like a button
  // would promise one. Singular, because it names ONE row.
  "patient.favouriteBadge": "Favourite patient",
  // The VIP mark. One accessible name everywhere the crown is drawn, because
  // the crown is the same clinic-scoped fact on the registry row, the identity
  // card and the context rail — never a per-surface synonym. It is a
  // CLASSIFICATION of the Patient, deliberately not worded like the
  // practitioner's own favourite star above.
  "patient.vipBadge": "VIP patient",
  // THE TEST-PATIENT MARK, the HL7 way: a synthetic chart is LABELLED wherever
  // it is shown, never hidden. One accessible name everywhere the chip is
  // drawn — the registry row, the digest, the ribbon, the card, every chooser
  // row — and one short word on the chip itself, because the chip stands
  // beside a code in a line that has no room for a sentence.
  "patient.testBadge": "Test patient",
  "patient.testChip": "TEST",
  // The two acts over the mark, and what a refused press should SAY. The
  // stale message promises the screen has already been brought current, so
  // the clinician re-reads one line instead of hunting for a reload control.
  // The title sheet's own row: the fact's name, and the definite
  // negative — "not VIP" is a stated classification, never an absence.
  "patientVip.mark": "Mark as VIP",
  "patientVip.unmark": "Remove VIP status",
  "patientVip.staleRevision":
    "The VIP status changed elsewhere. The current status is now shown; check it before changing it again.",
  "patientVip.failed": "The VIP status could not be changed. Try again.",

  // The shared Patient chooser (`shared/patients/PatientPicker`), which every
  // surface asking for one Patient out of the whole register opens. The two
  // leading groups are the practitioner's own shortcuts, so they are named for
  // WHY they lead rather than for the surface asking.
  "patientPicker.favouriteGroup": "Favourite patients",
  "patientPicker.recentGroup": "Recently viewed",
  "patientPicker.allGroup": "All patients",
  "patientPicker.placeholder": "No patient chosen",
  "patientPicker.noMatches": "No patient matches",
  "patientPicker.loadFailed": "Patients could not be loaded.",

  "patientMerge.open": "Merge duplicate",
  "patientMerge.title": "Merge duplicate patient",
  "patientMerge.description":
    "The selected chart will remain active. This chart will be marked as replaced; its existing clinical history will remain under its original patient reference.",
  "patientMerge.target": "Surviving patient",
  "patientMerge.reason": "Reason note (optional)",
  "patientMerge.checkingTarget": "Checking the selected patient…",
  "patientMerge.targetUnavailable":
    "The selected patient is no longer available.",
  "patientMerge.cancel": "Cancel",
  "patientMerge.confirm": "Merge patient",
  "patientMerge.merging": "Merging…",
  "patientMerge.staleRevision":
    "One of these patients changed. Choose the surviving patient again before merging.",
  "patientMerge.keyConflict":
    "This merge form was already used for a different patient. Close it and start again.",
  "patientMerge.targetNotActive":
    "The surviving patient is no longer active. Select another patient.",
  "patientMerge.notFound": "One of these patients is no longer available.",
  "patientMerge.rejected":
    "The merge was refused as entered. Check the selected patient and try again.",
  "patientMerge.failed": "The patients could not be merged. Try again.",

  // Registering a patient from the registry's Add. The panel says what the
  // server owns, because the one question a receptionist asks about a new chart
  // is where its number came from.
  "patientRegistration.title": "New patient",
  "patientRegistration.detail":
    "The patient code is assigned by the clinic when the chart is created. Everything here can be edited afterwards on the title sheet.",
  "patientRegistration.submit": "Register patient",
  "patientRegistration.submitting": "Registering…",
  // THE ONE PLACE A CHART IS MADE SYNTHETIC. The tick is worded as the act it
  // performs rather than as a property of the person, because it is answered
  // once and never again: the route takes the marker on create and refuses it
  // on every later edit. The hint says where the answer lands; the dialog says
  // what cannot be undone, and says it at the moment it becomes true.
  "patientRegistration.testPatient": "Register as a test patient",
  "patientRegistration.testPatientHint":
    "A synthetic chart for demonstration and checks. It carries the TEST label everywhere it is shown.",
  "patientRegistration.testConfirmTitle": "Save as a test patient?",
  "patientRegistration.testConfirmDetail":
    "This chart will be created as a test patient, and the mark can never be removed afterwards. A real person must be registered without it.",
  "patientRegistration.testConfirmSave": "Save as test patient",
  "patientRegistration.testConfirmFix": "Go back and fix",
  "patientRegistration.unsavedDetail":
    "This will discard the patient you were entering. No chart has been created.",
  "patientRegistration.unsavedIntent": "New patient",
  // A retry answered the original chart, or the clinic ran out of codes. Both
  // are about something that already exists rather than about this form.
  "patientRegistration.keyConflict":
    "These details do not match the patient this form already registered. Close the panel and start a new one.",
  "patientRegistration.codeExhausted":
    "This clinic has no patient codes left. Nothing was created; ask an administrator.",
  "patientRegistration.rejected":
    "The patient was refused as entered. Check the fields and try again.",
  "patientRegistration.failed":
    "The patient could not be registered. Try again.",

  /* The patient card workspace: the left digest-and-menu panel, and the EMR
     records registry the card opens on. */
  "patientCard.workspaceLabel": "Patient card",
  "patientCard.menuLabel": "Patient card sections",
  "patientCard.tabRecords": "EMR Records",
  "patientCard.tabRecordsDescription": "Encounters, notes and referrals",
  "patientCard.tabCases": "Cases",
  "patientCard.tabCasesDescription": "Episodes of care this work belongs to",
  "patientCard.tabFiles": "Files",
  "patientCard.tabFilesDescription": "Documents and images",
  "patientCard.tabMessages": "Messages",
  "patientCard.tabMessagesDescription": "Patient correspondence",
  "patientCard.tabStay": "Hospital Stays",
  "patientCard.tabStayDescription": "Planned and recorded admissions",
  "patientCard.tabSchedule": "Availability",
  "patientCard.tabScheduleDescription": "Availability and unavailable time",
  "patientCard.tabTasks": "Clinic tasks",
  "patientCard.tabTasksDescription":
    "Open and completed clinic work linked to this patient",
  "patientCard.tasksTitle": "Clinic tasks",
  "patientCard.tasksEmpty": "No clinic tasks linked to this patient",
  "patientCard.tasksEmptyDetail": "Clinic tasks linked to {name} appear here.",
  "patientCard.tabFinance": "Finance",
  "patientCard.tabFinanceDescription": "Treatment estimates and account context",
  "patientCard.tabLaboratory": "Laboratory Results and Vital Parameters",
  "patientCard.tabLaboratoryDescription":
    "Results, vital parameters, trends and report files",
  "patientCard.tabImaging": "Imaging",
  "patientCard.tabImagingDescription": "Hosted CT and MRI studies",
  "patientCard.tabTitleSheet": "Title Sheet",
  "patientCard.tabTitleSheetDescription": "Identity and administrative facts",
  "patientCard.tabMedical": "Medical Title Sheet",
  "patientCard.tabMedicalDescription":
    "Diagnoses, key readings and the patient summary",

  "patient.documents.previewBlank": "Preview consent form",
  "patient.documents.issueLink": "Issue signature link",
  "patient.documents.sendLink": "Send link",
  "patient.documents.deliveryFailed": "Delivery failed or is unavailable. Copy the link and share it with the patient.",
  "patient.documents.deliveryQueued": "Link delivery requested.",
  "patient.documents.sendTo": "Send to",
  "patient.documents.sendAllChannels": "All allowed channels",
  "patient.documents.noChannels": "No channels are allowed for communication.",
  "patient.documents.linkExpires": "Expires {when}",
  "patient.documents.linkExpired": "Link expired",
  "patient.documents.linkIssued": "Link issued",
  "patient.documents.linkHidden": "The address is shown only when issued; issue a new link to copy it.",
  "patient.documents.noLink": "No link issued",
  "patient.documents.revokeReason": "Reason for withdrawal",
  "patient.documents.revoke": "Revoke consent",
  "patient.documents.uploadSigned": "Upload signed consent",
  "patient.documents.submitReview": "Submit for clinic review",
  "patient.documents.title": "Documents",
  "patient.documents.forbidden": "This account cannot read patient documents.",
  "patient.documents.loading": "Loading documents…",
  "patient.documents.failed": "The documents block could not be loaded.",
  "patient.documents.downloadFailed": "The document could not be downloaded.",
  "patient.documents.identifierExists": "This identifier already has a record. Correct that record or attach scans to it.",
  "patient.documents.correctIdentifier": "Open existing record",
  "patient.documents.replaceIdentifierDraft": "Discard this draft and open the existing identifier record? If loading fails, the draft stays open.",
  "patient.documents.commandFailed":
    "The document command could not be completed. Try again.",
  "patient.documents.downloadForbidden": "This account cannot download patient documents.",
  "patient.documents.downloadGone": "That file is no longer stored. Refresh the block.",
  "patient.documents.downloadUnavailable": "Document storage did not answer. Try the download again shortly.",
  "patient.documents.createFailed": "The consent could not be created.",
  "patient.documents.issueFailed": "The signature link could not be issued.",
  "patient.documents.acceptFailed": "The consent could not be accepted.",
  "patient.documents.rejectFailed": "The consent could not be rejected.",
  "patient.documents.revokeFailed": "The consent could not be revoked.",
  "patient.documents.consentChanged": "This consent changed while the block was open. Refresh it and repeat the act.",
  "patient.documents.consentGone": "This consent no longer exists. Refresh the block.",
  "patient.documents.consentUnavailable": "The consent service did not answer. Try again shortly.",
  "patient.documents.consentRequestInvalid": "The request was rejected. Check the language and the reason.",
  "patient.documents.consentAlreadyActive": "This patient already has an active consent of this kind.",
  "patient.documents.consentNotPending": "Only a consent still awaiting signature can be given a link.",
  "patient.documents.consentNotSubmitted": "Only a submitted consent can be accepted or rejected.",
  "patient.documents.consentNotRevocable": "This consent has already been revoked or rejected.",
  "patient.documents.consentAccepted": "Accepted",
  "patient.documents.consentMissing": "Not accepted",
  "patient.documents.consentLanguage": "Consent language",
  "patient.documents.language.en": "English",
  "patient.documents.language.he": "Hebrew",
  "patient.documents.language.ru": "Russian",
  "patient.documents.createConsent": "Create consent",
  "patient.documents.creating": "Creating…",
  "patient.documents.downloadBlank": "Download blank",
  "patient.documents.downloadSigned": "Download signed consent",
  "patient.documents.linkReady": "Signature link",
  "patient.documents.accept": "Accept consent",
  "patient.documents.accepting": "Accepting…",
  "patient.documents.reject": "Reject",
  "patient.documents.rejecting": "Rejecting…",
  "patient.documents.rejectReason": "Reason for rejection",
  "patient.documents.rejectTitle": "Reject this consent?",
  "patient.documents.revokeTitle": "Revoke this consent?",
  "patient.documents.rejectionReason": "Rejection reason",
  "patient.documents.consentSection": "Personal data consent",
  "patient.documents.governmentIds": "Government IDs and passports",
  // What the register AMOUNTS TO, stated whether or not the rows above it are
  // filtered: a register showing one of three documents because two expired
  // must still say three, or the filter quietly becomes the record.
  // Said where "none recorded" would be a lie: the register holds records and
  // the filter is hiding all of them. A count of two over "none recorded" is
  // the filter becoming the record.
  "patient.documents.allGovernmentIdsExpired":
    "Every ID document here has expired.",
  "patient.documents.allInsuranceExpired":
    "Every insurance policy here has expired.",
  "patient.documents.insurancePolicies": "Insurance policies",
  "patient.documents.addGovernmentId": "Add ID document",
  "patient.documents.addInsurance": "Add insurance policy",
  "patient.documents.noGovernmentIds": "No ID documents recorded yet.",
  "patient.documents.noInsurancePolicies":
    "No insurance policies recorded yet.",
  "patient.documents.downloadScan": "Download scan",
  "patient.documents.cancel": "Cancel",
  "patient.documents.source": "Document source",
  "patient.documents.source.upload": "Upload a new scan",
  "patient.documents.source.existing": "Use an existing file",
  "patient.documents.pdfPage": "PDF page",
  "patient.documents.scanEditor": "Document scan",
  "patient.documents.dropZone":
    "Drag a scan here, paste it from the clipboard or choose a file.",
  "patient.documents.dropZoneMore":
    "Both sides of a card or several passport pages can be added as separate images; they are saved as one document.",
  "patient.documents.chooseFiles": "Choose files",
  "patient.documents.addPage": "Add another page",
  "patient.documents.pages": "Document pages",
  "patient.documents.pageName": "Page {index}",
  "patient.documents.rotateLeft": "Rotate left",
  "patient.documents.rotateRight": "Rotate right",
  "patient.documents.cropPage": "Crop",
  "patient.documents.cropReset": "Reset crop",
  "patient.documents.cropMove": "Move the crop frame",
  "patient.documents.cropEdge.left": "Left edge of the crop",
  "patient.documents.cropEdge.top": "Top edge of the crop",
  "patient.documents.cropEdge.right": "Right edge of the crop",
  "patient.documents.cropEdge.bottom": "Bottom edge of the crop",
  "patient.documents.cropCorner.topLeft": "Top left corner of the crop",
  "patient.documents.cropCorner.topRight": "Top right corner of the crop",
  "patient.documents.cropCorner.bottomLeft": "Bottom left corner of the crop",
  "patient.documents.cropCorner.bottomRight": "Bottom right corner of the crop",
  "patient.documents.removePage": "Remove page {index}",
  "patient.documents.movePageEarlier": "Move page {index} earlier",
  "patient.documents.movePageLater": "Move page {index} later",
  "patient.documents.pagesMerged":
    "Pages are saved as separate files in this order. All files are included in recognition.",
  "patient.documents.pagePreparing": "Preparing the page…",
  "patient.documents.pageFailed":
    "The browser could not prepare this page. It will be uploaded as the original file.",
  "patient.documents.unsupportedFiles": "Only images and PDF files can be added.",
  "patient.documents.existingFile": "Existing patient file",
  "patient.documents.selectExisting": "Select a file",
  "patient.documents.saving": "Saving…",
  "patient.documents.saveDocument": "Save document",
  "patient.documents.suggestFromScan": "Suggest from scan",
  "patient.documents.suggesting": "Reading scan…",
  "patient.documents.suggestionApplied":
    "Suggestions filled the form. Review every field before saving.",
  "patient.documents.suggestionReview":
    "Automatic suggestions are drafts only. Check the scan and edit the fields before saving.",
  "patient.documents.recognitionUnavailable": "Document recognition is temporarily unavailable. Try again later or fill the fields manually.",
  "patient.documents.suggestionFailed":
    "The scan could not be read automatically. Fill the fields manually.",
  "patient.documents.suggestionWarning": "Model warning: {warning}",
  "patient.documents.previewEmpty":
    "Choose a scan to preview it beside the form.",
  "patient.documents.uploadTitle.governmentId": "Government ID",
  "patient.documents.uploadTitle.insurance": "Insurance policy",
  "patient.documents.governmentId.type": "Document type",
  "patient.documents.governmentId.type.travel_passport": "Travel passport",
  "patient.documents.governmentId.type.diplomatic_passport": "Diplomatic passport",
  "patient.documents.governmentId.type.service_passport": "Service passport",
  "patient.documents.governmentId.type.temporary_identity": "Temporary identity document",
  "patient.documents.governmentId.type.military_id": "Military identity document",
  "patient.documents.governmentId.type.seafarer_id": "Seafarer identity document",
  "patient.documents.governmentId.type.stateless_document": "Stateless person document",
  "patient.documents.governmentId.type.refugee_document": "Refugee or asylum document",
  "patient.documents.governmentId.type.snils": "SNILS",
  "patient.documents.governmentId.type.tax_number": "Personal tax number",
  "patient.documents.documentSeries": "Series",
  "patient.documents.issuerCode": "Issuing authority code",
  "patient.documents.personalNumber": "Personal number",
  "patient.documents.designation": "Document designation",
  "patient.documents.governmentId.type.passport": "Passport",
  "patient.documents.governmentId.type.national_id_card": "National ID card",
  "patient.documents.governmentId.type.residence_permit": "Residence permit",
  "patient.documents.governmentId.type.driving_licence": "Driving licence",
  "patient.documents.governmentId.type.birth_certificate": "Birth certificate",
  "patient.documents.governmentId.type.other_government_id":
    "Other government ID",
  "patient.documents.documentNumber": "Document number",
  "patient.documents.issuingCountry": "Issuing country",
  "patient.documents.nameOnDocument": "Name on document",
  "patient.documents.birthDate": "Date of birth",
  "patient.documents.issueDate": "Issue date",
  "patient.documents.expiryDate": "Expiry date",
  "patient.documents.issuingAuthority": "Issuing authority",
  "patient.documents.insurance.type": "Policy type",
  "patient.documents.insurance.type.israel_kupat_holim_basic":
    "Israel kupat holim basic coverage",
  "patient.documents.insurance.type.israel_shaban_supplementary":
    "Israel supplementary health plan",
  "patient.documents.insurance.type.israel_private_commercial":
    "Israel private commercial insurance",
  "patient.documents.insurance.type.israel_tourist_travel":
    "Israel tourist or travel insurance",
  "patient.documents.insurance.type.israel_student": "Israel student insurance",
  "patient.documents.insurance.type.israel_foreign_worker":
    "Israel foreign worker insurance",
  "patient.documents.insurance.type.israel_international_private":
    "Israel international private insurance",
  "patient.documents.insurance.type.statutory_public_health":
    "Statutory public health coverage",
  "patient.documents.insurance.type.social_health_fund": "Social health fund",
  "patient.documents.insurance.type.supplementary_health_plan":
    "Supplementary health plan",
  "patient.documents.insurance.type.commercial_private_health":
    "Commercial private health insurance",
  "patient.documents.insurance.type.employer_group_health":
    "Employer group health plan",
  "patient.documents.insurance.type.travel_health": "Travel health insurance",
  "patient.documents.insurance.type.student_health": "Student health insurance",
  "patient.documents.insurance.type.foreign_worker_health":
    "Foreign worker health insurance",
  "patient.documents.insurance.type.international_expat_health":
    "International or expat health insurance",
  "patient.documents.insurance.type.other_health_coverage":
    "Other health coverage",
  "patient.documents.insurerName": "Insurer",
  "patient.documents.planName": "Plan name",
  "patient.documents.policyNumber": "Policy number",
  "patient.documents.memberNumber": "Member number",
  "patient.documents.groupNumber": "Group number",
  "patient.documents.validFrom": "Valid from",
  "patient.documents.validTo": "Valid to",
  "patient.documents.status.prepared": "Prepared",
  "patient.documents.status.sent": "Awaiting signature",
  "patient.documents.status.submitted": "Uploaded, waiting for clinic review",
  "patient.documents.status.accepted": "Accepted",
  "patient.documents.status.rejected": "Rejected",
  "patient.documents.status.revoked": "Revoked",
  "patient.documents.public.title": "Sign patient document",
  /* The medical title sheet: the chart's clinical face on one page, read from
     the sections that own each block. Its tiles, its leaves and the doors at
     their feet are named here beside the section that offers it. */
  "medicalSheet.title": "Medical title sheet",
  "medicalSheet.subtitle":
    "The chart at a glance: diagnoses, latest readings and open work, read from the sections that own them",
  "medicalSheet.loading": "Reading the chart…",
  "medicalSheet.notReadable": "Not readable by this account",
  "medicalSheet.recordsForbidden":
    "This account cannot read clinical records, so this part of the sheet is not shown.",
  "medicalSheet.statPatient": "Patient",
  "medicalSheet.ageUnknown": "Age not recorded",
  "medicalSheet.curatorNamed": "Curator: {name}",
  "medicalSheet.curatorNone": "No curator assigned",
  "medicalSheet.statDiagnoses": "Active diagnoses",
  "medicalSheet.diagnosesCaption": "{total} on record · {chronic} chronic",
  "medicalSheet.statCases": "Open cases",
  "medicalSheet.casesCaption": "Active and planned",
  "medicalSheet.statStay": "Hospital stay",
  "medicalSheet.stayNone": "None",
  "medicalSheet.stayNoneCaption": "No current or planned stay",
  "medicalSheet.statLastVisit": "Last encounter",
  "medicalSheet.visitNone": "None",
  "medicalSheet.visitNoneCaption": "No encounters recorded",
  "medicalSheet.statLaboratory": "Out-of-range results",
  "medicalSheet.laboratoryWindow": "Last 90 days",
  "medicalSheet.laboratoryCritical": "{count} critical · last 90 days",
  "medicalSheet.laboratoryClear": "Nothing out of range",
  "medicalSheet.laboratoryNoResults": "No results yet",
  "medicalSheet.diagnosesTitle": "Diagnoses",
  "medicalSheet.diagnosesEmpty": "No diagnoses recorded for this patient.",
  "medicalSheet.diagnosesFailed": "The diagnoses could not be read.",
  "medicalSheet.encounters": "Encounters",
  "medicalSheet.vitalsTitle": "Latest vital parameters",
  "medicalSheet.vitalsEmpty": "No vital parameters recorded.",
  "medicalSheet.vitalsFailed": "The vital parameters could not be read.",
  "medicalSheet.laboratoryTitle": "Results needing attention",
  "medicalSheet.laboratoryEmpty": "No out-of-range laboratory results.",
  "medicalSheet.laboratoryFailed": "The laboratory results could not be read.",
  "medicalSheet.casesTitle": "Open cases",
  "medicalSheet.casesEmpty": "No open cases.",
  "medicalSheet.casesFailed": "The cases could not be read.",
  "medicalSheet.staysTitle": "Hospital stays",
  "medicalSheet.staysEmpty": "No current or planned hospital stay.",
  "medicalSheet.staysFailed": "The hospital stays could not be read.",
  "medicalSheet.encountersTitle": "Recent encounters",
  "medicalSheet.encountersEmpty": "No encounters recorded.",
  "medicalSheet.encountersFailed": "The encounters could not be read.",
  "medicalSheet.filesTitle": "Latest documents",
  "medicalSheet.filesEmpty": "No documents attached.",
  "medicalSheet.filesFailed": "The documents could not be read.",
  "medicalSheet.openRecords": "All encounters",
  "medicalSheet.openLaboratory": "All results",
  "medicalSheet.openCases": "All cases",
  "medicalSheet.openStays": "All stays",
  "medicalSheet.openFiles": "All files",
  "medicalSheet.undated": "Undated",
  "medicalSheet.referenceRange": "Reference range: {range}",

  /* The chart's EMR section is the ENCOUNTER JOURNAL, and its copy lives on the
     `emr` surface beside the endpoint it renders (`emrJournal.*`). What stays
     here is the card's own chrome: the menu, its section names and the patient
     facts the digest shows. */
  // The entity is a CASE. Not a journey, not a trip, not an episode: a Case
  // groups clinical work that a clinician decided belongs together, and the
  // grouping is OPTIONAL. Copy that implies every Record lives in one, or that
  // a Case is travel, describes a different product.
  "case.loading": "Loading cases",
  "case.loadFailed": "Cases could not be loaded.",
  "case.datesOutOfOrder": "The end date is before the start date.",
  // The reload REPLACES the form with what is stored now, so the sentence
  // asks for the change to be re-applied rather than promising the typed text
  // survives the press. It is the file editor's answer, in this domain's words.
  "case.staleRevision":
    "This case changed elsewhere. Reload it to see what is stored now, then apply your change to it.",
  "case.transitionRefused":
    "This case is no longer in a status that allows that.",
  "case.saveFailed": "The case could not be saved. Try again.",
  "case.gone": "This case is no longer available.",
  "case.unavailable": "Cases are unavailable right now.",
  "case.status.planned": "Planned",
  "case.status.active": "Active",
  "case.status.completed": "Completed",
  "case.status.cancelled": "Cancelled",
  // The Cases section on the chart: the registry, its columns, and the panel
  // that edits one Case. The section is titled with the entity's own name, and
  // the subtitle carries the clinical sense the name alone cannot: a Case is
  // one episode of care, and the work of that episode is read under it.
  "case.sectionTitle": "Cases",
  "case.sectionSubtitle":
    "Every case recorded for this patient. A case is one episode of care: it gathers the encounters, records and referrals that belong to it.",
  "case.empty": "No cases recorded",
  "case.emptyDetail":
    "No clinical work has been recorded for this patient yet. The first encounter or EMR entry opens the first case.",
  "case.noMatches": "No case matches the current filters",
  "case.columnTitle": "Case",
  "case.columnCode": "Code",
  "case.columnCurator": "Curator",
  "case.columnStatus": "Status",
  "case.columnPeriod": "Period",
  "case.columnColour": "Colour",
  "case.columnActions": "Actions",
  "case.noPeriod": "No dates",
  "case.transitionOn": "{action}: {title}",
  "case.periodRange": "{from} – {to}",
  "case.periodFrom": "From {from}",
  "case.periodUntil": "Until {to}",
  "case.new": "New case",
  "case.edit": "Edit case",
  "case.editNamed": "Edit case {name}",
  "case.openNamed": "Open case {name}",
  "case.title": "Title",
  "case.comment": "Comment",
  "case.startDate": "Start date",
  "case.endDate": "End date",
  "case.create": "Create case",
  "case.save": "Save case",
  "case.titleRequired": "A case needs a title.",
  "case.reload": "Reload this case",
  "case.reloaded":
    "Reloaded from the server. Review the changes and save again.",
  "case.reloadFailed":
    "The saved revision could not be loaded. Your changes are still here.",
  "case.unsavedIntent": "Case details",
  "case.activate": "Activate",
  "case.complete": "Complete",
  "case.cancelCase": "Cancel case",
  // The colour is a GROUPING AID chosen by a clinician, and it is named on
  // every surface that shows it — a colour available only as a colour would
  // be unreadable to a clinician who cannot tell the swatches apart.
  "case.colour": "Colour",
  "case.colourNone": "No colour",
  "case.colour.red": "Red",
  "case.colour.orange": "Orange",
  "case.colour.green": "Green",
  "case.colour.teal": "Teal",
  "case.colour.blue": "Blue",
  "case.colour.purple": "Purple",

  // Hospital Stays: an INDEPENDENT Patient track. The copy states that
  // independence because layout alone cannot: a chart section beside Case-aware
  // surfaces invites the reading that a stay belongs to a Case, and the
  // contract stores no such relation.
  "stay.sectionTitle": "Hospital stays",
  "stay.independent":
    "Hospital stays stand on their own. They are not grouped under cases.",
  "stay.loading": "Loading hospital stays…",
  "stay.loadFailed": "Hospital stays could not be loaded.",
  "stay.empty": "No hospital stays recorded",
  "stay.emptyDetail":
    "Nothing is expected here. A stay is recorded only when one is planned.",
  "stay.new": "Plan a hospital stay",
  "stay.create": "Create stay",
  "stay.save": "Save stay",
  "stay.cancelEditor": "Discard changes",
  "stay.unsavedIntent": "Hospital stay plan",
  "stay.editNamed": "Edit the stay plan at {facility}",
  "stay.transitionOn": "{action}: {facility}",
  "stay.facility": "Facility",
  "stay.facilityRequired": "Name the facility before saving.",
  "stay.wardBedNote": "Ward or bed note (optional)",
  "stay.plannedAdmission": "Planned admission (clinic time)",
  "stay.plannedDischarge": "Planned discharge (clinic time)",
  "stay.timesRequired": "Enter both planned times before saving.",
  "stay.timesOutOfOrder":
    "The planned discharge must be after the planned admission.",
  "stay.plannedWindow": "Planned:",
  "stay.actualAdmission": "Admitted:",
  "stay.actualDischarge": "Discharged:",
  "stay.admit": "Admit",
  "stay.discharge": "Discharge",
  "stay.cancelStay": "Cancel stay",
  "stay.status.planned": "Planned",
  "stay.status.admitted": "Admitted",
  "stay.status.discharged": "Discharged",
  "stay.status.cancelled": "Cancelled",
  "stay.staleRevision":
    "This stay was changed elsewhere. Reload it before acting.",
  "stay.reload": "Reload this stay",
  // Not "save again" like the other reload notes: this one also answers a
  // refused TRANSITION, which has no form to save.
  "stay.reloaded": "Reloaded from the server. Review the stay and try again.",
  "stay.reloadFailed":
    "The saved revision could not be loaded. Your changes are still here.",
  "stay.transitionRefused":
    "This stay is no longer in a state that allows that action.",
  "stay.gone": "This stay is no longer available.",
  "stay.unavailable": "Hospital stays are unavailable right now. Try again.",
  "stay.saveFailed": "The stay could not be saved. Try again.",
} as const;
